/* eslint-disable */
import React from "react";
import {
  Card,
  Group,
  Title,
  Text,
  Stack,
  Avatar,
  Button,
  createStyles,
  Loader,
  Center,
  TextInput,
} from "@mantine/core";

import { PieChart, Pie, Cell } from "recharts";
import { useGetAttendanceAdminV2 } from "api/attendance/attendanceDashboard";
import { useForm } from "@mantine/form";
import { useGetAttendanceSummary } from "api/attendance/getAttendanceSummary";
import { useNavigate } from "react-router-dom";

const useStyles = createStyles((theme) => ({
  card: {
    height: "100%",
    // minHeight: "70vh",
    borderRadius: 8,
    padding: theme.spacing.md,
    // boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
  },
  gaugeWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  centerText: {
    textAlign: "center",
    pointerEvents: "none",
    transform: "translateY(12px)",
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 10,
    display: "inline-block",
    marginRight: 10,
  },
  footerRow: {
    marginTop: theme.spacing.md,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

interface Segment {
  label: string;
  value: number;
  color: string;
  percent?: number;
}

interface MantineAttendanceGaugeProps {
  month?: number;
  year?: number;
  avatars?: string[];
}

export default function AttendanceWidget({
  month = 8,
  year = 2025,
  avatars = [],
}: MantineAttendanceGaugeProps) {
  const { classes } = useStyles();
  const today = new Date().toISOString().slice(0, 10);

  const form = useForm({
    initialValues: {
      date: today,
    },
  });

  const selectedDate = form.values.date;

  const {
    data: apiData,
    isLoading,
    isError,
  } = useGetAttendanceAdminV2({
    type: "date",
    date: selectedDate,
  });
  const navigate = useNavigate();
  const { data: summary } = useGetAttendanceSummary(selectedDate);
  const PieChartAny: any = PieChart;
  const PieAny: any = Pie;
  const CellAny: any = Cell;

  if (isLoading) {
    return (
      <Card className={classes.card} radius="md" shadow="sm">
        <Group position="apart" align="flex-start">
          <Title order={4}>Attendance Overview</Title>
          <Button variant="outline" compact size="xs">
            Today
          </Button>
        </Group>

        <Center style={{ height: 160 }}>
          <Loader />
        </Center>
      </Card>
    );
  }

  const attendanceArray: any[] =
    apiData?.data?.attendanceData && Array.isArray(apiData.data.attendanceData)
      ? apiData.data.attendanceData
      : apiData?.data?.attendanceData &&
        !Array.isArray(apiData.data.attendanceData)
      ? Array.isArray(
          apiData.data.attendanceData &&
            !Array.isArray(apiData.data.attendanceData)
        )
        ? apiData.data.attendanceData &&
          !Array.isArray(apiData.data.attendanceData)
        : [
            apiData.data.attendanceData &&
              !Array.isArray(apiData.data.attendanceData),
          ]
      : [];

  if (
    attendanceArray.length === 0 &&
    apiData?.data &&
    Array.isArray(apiData.data)
  ) {
    const maybeArr = apiData.data;
    if (Array.isArray(maybeArr)) {
      // @ts-ignore
      attendanceArray.push(...maybeArr);
    }
  }

  const totalEmployees = attendanceArray.length;
  let presentCount = 0;
  let absentCount = 0;

  const presentAvatars: string[] = [];

  attendanceArray.forEach((item) => {
    const attendanceMap =
      item?.attendance || item?.data?.attendance || item?.attendanceData || {};
    const dayRecord = attendanceMap ? attendanceMap[selectedDate] : undefined;

    if (dayRecord && typeof dayRecord.status === "string") {
      const st = dayRecord.status.toLowerCase();
      if (st === "present") {
        presentCount += 1;
        const avatarUrl =
          item?.employee?.avatar || item?.employee?.profilePicture || null;
        if (avatarUrl) presentAvatars.push(avatarUrl);
      } else {
        absentCount += 1;
      }
    } else {
      absentCount += 1;
    }
  });

  const rawSegments: Segment[] = [
    {
      label: "Present",
      value: summary?.data.present.count ?? 0,
      percent: summary?.data.present.percentageChange ?? 0,
      color: "#10b981",
    },
    {
      label: "Absent",
      value: summary?.data.absent.count ?? 0,
      percent: summary?.data.absent.percentageChange ?? 0,
      color: "#ef4444",
    },
  ];

  const totalValue = rawSegments.reduce((s, r) => s + r.value, 0) || 1;

  const segments = rawSegments.map((s) => ({
    ...s,
    percent: (s.value / totalValue) * 100,
  }));

  const pieData = segments.map((s) => ({ name: s.label, value: s.value }));

  return (
    <Card className={classes.card} radius="md" shadow="sm">
      <Group position="apart" align="flex-start">
        <Title order={3} style={{ fontSize: "18px" }}>
          Attendance Overview
        </Title>

        {/* <Button variant="outline" compact size="xs">
          Today
        </Button> */}
        <TextInput
          withAsterisk
          type="date"
          // label="Date"
          min="2000-01-01"
          max={today}
          {...form.getInputProps("date")}
          style={{ minWidth: 160 }}
        />
      </Group>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // gap: 18,
          alignItems: "center",
          // marginTop: 30,
        }}
      >
        {totalEmployees === 0 ? (
          <Center style={{ height: 160 }}>
            <Text color="dimmed">No attendance data for this date.</Text>
          </Center>
        ) : (
          <>
            <PieChartAny width={300} height={200}>
              <PieAny
                data={pieData}
                startAngle={0}
                endAngle={360}
                innerRadius={50}
                outerRadius={90}
                paddingAngle={0}
                dataKey="value"
                isAnimationActive={false}
              >
                {segments.map((seg, idx) => (
                  <CellAny key={`cell-${idx}`} fill={seg.color} stroke="none" />
                ))}
              </PieAny>
            </PieChartAny>
            <div style={{ flex: 1, minWidth: 170 }}>
              <Stack>
                {segments.map((s) => (
                  <Group
                    key={s.label}
                    position="apart"
                    style={{ width: "100%" }}
                  >
                    <Group spacing={8}>
                      <span
                        className={classes.legendDot}
                        style={{ background: s.color }}
                      />
                      <div>
                        <Text size="sm">{s.label}</Text>
                        <Text size="xs" color="dimmed">
                          {Math.round(s.percent!)}%
                        </Text>
                      </div>
                    </Group>
                    <Text size="sm">{s.value}</Text>
                  </Group>
                ))}

                <Group position="apart" style={{ marginTop: 6 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: 10,
                        borderRadius: 8,
                      }}
                    >
                      <Avatar.Group spacing="sm">
                        {presentAvatars.slice(0, 3).map((url, i) => (
                          <Avatar
                            key={url || `avatar-${i}`}
                            src={url}
                            size={30}
                            radius="xl"
                          />
                        ))}
                        {presentAvatars.length > 3 && (
                          <Avatar radius="xl" size={30}>
                            +{presentAvatars.length - 3}
                          </Avatar>
                        )}
                        {presentAvatars.length === 0 &&
                          avatars
                            .slice(0, 3)
                            .map((url, i) => (
                              <Avatar
                                key={url || `a2-${i}`}
                                src={url}
                                size={30}
                                radius="xl"
                              />
                            ))}
                      </Avatar.Group>
                    </div>

                    <Text size="xs" color="dimmed">
                      Total Users: {totalEmployees}
                    </Text>
                  </div>

                  <Button
                    variant="light"
                    size="xs"
                    onClick={() => navigate("/attendance-admin")}
                  >
                    View Details
                  </Button>
                </Group>
              </Stack>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
